import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Divider,
  InputNumber,
  Space,
  Table,
  Upload,
  message,
  Typography,
  Modal,
  Tag,
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  InboxOutlined,
  FileExcelOutlined,
  MinusCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import {
  getAllBanks,
  getStatementById,
  createStatement,
  updateStatement,
  processStatementFile,
  deleteStatement,
} from "../../../api/accounting";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Title } = Typography;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

const CreateStatement = ({
  statementId,
  selectedBrand = null,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [fileProcessingModalVisible, setFileProcessingModalVisible] =
    useState(false);
  const [fileObj, setFileObj] = useState(null);
  const navigate = useNavigate();
  const [bankBalance, setBankBalance] = useState(0);
  const [latestStatementBalance, setLatestStatementBalance] = useState(0);
  
  // Ref to store Form.List remove function so delete handler can use it
  const formListRemoveRef = useRef(null);
  
  // State to force re-render when transactions are deleted
  const [transactionDeletionKey, setTransactionDeletionKey] = useState(0);

  const fetchStatementDetails = useCallback(
    async (id) => {
      if (!id) {
        console.error("Cannot fetch statement details: ID is undefined");
        return;
      }
      try {
        setLoading(true);
        const statement = await getStatementById(id);

        // Format transactions for form - preserve all transaction data including IDs
        const formattedTransactions = statement.transactions.map(
          (transaction, index) => {
            // CRITICAL: Ensure each transaction has a UNIQUE key, even if IDs are duplicates
            // Use ID + index combination to guarantee uniqueness
            // This ensures React treats each transaction as a separate row, even if they're duplicates
            const txKey = transaction.id 
              ? `backend-${transaction.id}-${index}` 
              : `backend-new-${index}`;
            
            // Ensure description is a string, not an object
            let descriptionValue = transaction.description || "";
            if (typeof descriptionValue === 'object' && descriptionValue !== null) {
              descriptionValue = descriptionValue.description || descriptionValue.toString() || "";
            }
            if (typeof descriptionValue !== 'string') {
              descriptionValue = String(descriptionValue || "");
            }
            
            return {
              ...transaction, // This includes id, allocated_amount, overpayment_amount, etc.
              key: txKey, // Unique key combining ID and index
              transaction_date: dayjs(transaction.transaction_date),
              description: descriptionValue, // Ensure description is always a string
              // Explicitly preserve ID to ensure it's not lost
              id: transaction.id !== undefined && transaction.id !== null ? parseInt(transaction.id) : undefined,
            };
          }
        );

        // Check for duplicate IDs
        const ids = formattedTransactions.map(t => t.id).filter(Boolean);
        const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
        if (duplicateIds.length > 0) {
          console.error('ERROR: Duplicate transaction IDs found:', duplicateIds);
        }

        console.log('Fetched statement transactions:', {
          count: formattedTransactions.length,
          transactions: formattedTransactions.map(t => ({
            id: t.id,
            description: t.description,
            allocated_amount: t.allocated_amount,
            overpayment_amount: t.overpayment_amount,
            key: t.key
          }))
        });

        // Set form values
        form.setFieldsValue({
          bank: statement.bank,
          statement_date: dayjs(statement.statement_date),
          start_date: dayjs(statement.start_date),
          // end_date removed - using statement_date instead
          opening_balance: statement.opening_balance,
          closing_balance: statement.closing_balance,
          notes: statement.notes,
          transactions: formattedTransactions,
        });

        setIsEditing(true);
        setInitialValues(statement);
        setLoading(false);

        // Return the statement for further processing
        return statement;
      } catch (error) {
        console.error("Error fetching statement details:", error);
        message.error("Failed to fetch statement details");
        setLoading(false);
        return null;
      }
    },
    [form]
  );

  useEffect(() => {
    // Only fetch banks when selectedBrand changes
    if (selectedBrand) {
      const filters = { brand: selectedBrand.id };
      getAllBanks(filters)
        .then((response) => {
          if (response && response.data) {
            setBanks(response.data);
          } else if (Array.isArray(response)) {
            setBanks(response);
          }
        })
        .catch((error) => {
          console.error("Error fetching banks:", error);
          message.error("Failed to fetch banks");
        });
    }
  }, [selectedBrand]); // Only depend on selectedBrand

  // Separate useEffect for fetching statement details
  useEffect(() => {
    if (statementId && banks.length > 0) {
      fetchStatementDetails(statementId).then((statement) => {
        if (statement) {
          // Set both balance values after fetching statement details
          const bankDetails = banks.find((bank) => bank.id === statement.bank);
          if (bankDetails) {
            if (bankDetails.account_balance !== undefined) {
              setBankBalance(parseFloat(bankDetails.account_balance));
            }

            if (bankDetails.latest_statement_closing_balance !== undefined) {
              setLatestStatementBalance(
                parseFloat(bankDetails.latest_statement_closing_balance)
              );
            }
          }
        }
      });
    }
  }, [statementId, fetchStatementDetails, banks]);

  const onFinish = async (values) => {
    try {
      if (!selectedBrand) {
        message.error("Please select a brand");
        return;
      }

      // CRITICAL: Get form values DIRECTLY from form instance
      // The 'values' parameter might be stale if Form.List state is out of sync
      const currentFormValues = form.getFieldsValue();
      const transactionsToUse = currentFormValues.transactions || values.transactions || [];
      
      console.log('=== ONFINISH - FORM VALUES ===');
      console.log('Values parameter transactions:', values.transactions?.length || 0);
      console.log('Direct form.getFieldsValue().transactions:', currentFormValues.transactions?.length || 0);
      console.log('Using transactions count:', transactionsToUse.length);
      console.log('Transaction IDs being used:', transactionsToUse.map(t => ({
        id: t.id,
        description: t.description,
        hasId: !!t.id,
        key: t.key,
        allocated_amount: t.allocated_amount,
        overpayment_amount: t.overpayment_amount
      })) || []);
      
      // Use the direct form values for the rest of the function
      const finalValues = {
        ...values,
        transactions: transactionsToUse
      };

      setLoading(true);

      // Format data for API
      // Note: end_date is removed from form - use statement_date as end_date
      const formattedData = {
        ...finalValues,
        statement_date: finalValues.statement_date.format("YYYY-MM-DD"),
        start_date: finalValues.start_date.format("YYYY-MM-DD"),
        end_date: finalValues.statement_date.format("YYYY-MM-DD"), // Use statement_date as end_date
        brand: selectedBrand?.id || 1,
        notes: finalValues.notes || " ",
        // Make opening_balance and closing_balance optional (default to 0 if not provided)
        opening_balance: finalValues.opening_balance ?? 0,
        closing_balance: finalValues.closing_balance ?? 0,
      };

      console.log("Formatted Data for the API NOTES: ", finalValues.notes);

      // Format transactions
      // IMPORTANT: Include transaction ID if it exists (for updates) so backend can identify existing transactions
      // CRITICAL: Only include transactions that are still in the form (user removed others by filtering them out)
      console.log('=== FORMATTING TRANSACTIONS FOR BACKEND ===');
      console.log('Total transactions in form:', transactionsToUse.length);
      console.log('Transaction details before formatting:', transactionsToUse.map((t, idx) => ({
        index: idx,
        id: t.id,
        key: t.key,
        description: t.description,
        allocated_amount: t.allocated_amount,
        overpayment_amount: t.overpayment_amount
      })));
      
      // CRITICAL: Filter out any null/undefined transactions and ensure we only send transactions that are still in the form
      // This ensures deleted transactions are not sent to the backend
      const validTransactions = transactionsToUse.filter((transaction) => {
        if (!transaction) return false;
        // Additional validation: ensure transaction has required fields
        if (!transaction.transaction_date) {
          console.warn('Transaction missing date, skipping:', transaction);
          return false;
        }
        return true;
      });
      
      console.log('=== FILTERING TRANSACTIONS FOR BACKEND ===');
      console.log('Original count:', transactionsToUse.length);
      console.log('Valid transactions count:', validTransactions.length);
      console.log('Transactions being sent:', validTransactions.map((t, idx) => ({
        index: idx,
        id: t.id,
        key: t.key,
        description: t.description,
        allocated_amount: t.allocated_amount,
        overpayment_amount: t.overpayment_amount
      })));
      
      const formattedTransactions = validTransactions.map((transaction, index) => {
          // Ensure description is a string, not an object
          let descriptionValue = transaction.description || "";
          if (typeof descriptionValue === 'object' && descriptionValue !== null) {
            descriptionValue = descriptionValue.description || descriptionValue.toString() || "";
          }
          if (typeof descriptionValue !== 'string') {
            descriptionValue = String(descriptionValue || "");
          }
          
          // Handle transaction_date - could be dayjs object or string
          let formattedDate;
          if (transaction.transaction_date) {
            if (typeof transaction.transaction_date === 'string') {
              // Already a string, use as-is (should be YYYY-MM-DD format)
              formattedDate = transaction.transaction_date;
            } else if (transaction.transaction_date.format) {
              // dayjs object, format it
              formattedDate = transaction.transaction_date.format("YYYY-MM-DD");
            } else {
              // Fallback - try to parse as date
              formattedDate = dayjs(transaction.transaction_date).format("YYYY-MM-DD");
            }
          } else {
            // Default to today if no date
            formattedDate = dayjs().format("YYYY-MM-DD");
          }
          
          const formatted = {
            transaction_date: formattedDate,
            detail: transaction.detail || "DEBIT",
            type: transaction.type || "ACH_DEBIT",
            description: descriptionValue,
            amount: parseFloat(transaction.amount || 0),
            reference: transaction.reference || "N/A",
            check_or_slip: transaction.check_or_slip || "",
            duplicate_status: transaction.duplicate_status || "NEW_ENTRY",
            possible_duplicate_or_update_of:
              transaction.possible_duplicate_or_update_of || null,
            business_hash: transaction.business_hash || null,
            row_hash: transaction.row_hash || null,
          };
          
          // CRITICAL: Include ID if transaction exists (for updates)
          // Without the ID, backend will treat it as a new transaction and try to delete the existing one
          const txId = transaction.id !== undefined && transaction.id !== null 
            ? parseInt(transaction.id) 
            : null;
          
          if (txId && !isNaN(txId) && txId > 0) {
            formatted.id = txId;
            console.log(`✅ Transaction ${index}: Including ID ${txId} for:`, transaction.description);
          } else {
            // Check if this is an existing transaction that should have an ID
            if (transaction.key && transaction.key.startsWith('backend-')) {
              // Extract ID from key as fallback: backend-{id}
              const keyIdMatch = transaction.key.match(/backend-(\d+)/);
              if (keyIdMatch && keyIdMatch[1]) {
                const fallbackId = parseInt(keyIdMatch[1]);
                if (!isNaN(fallbackId) && fallbackId > 0) {
                  formatted.id = fallbackId;
                  console.warn(`⚠️ Transaction ${index}: Using fallback ID ${fallbackId} from key for:`, transaction.description);
                } else {
                  console.error(`❌ Transaction ${index}: Existing transaction missing ID and key doesn't contain valid ID:`, transaction);
                }
              } else {
                console.error(`❌ Transaction ${index}: Existing transaction missing ID! This will cause backend to try to delete it:`, transaction);
              }
            } else {
              // New transaction - no ID needed
              console.log(`🆕 Transaction ${index}: New transaction (no ID needed):`, transaction.description);
            }
          }
          return formatted;
        });
      
      console.log('=== FORMATTED TRANSACTIONS ===');
      console.log('Total formatted transactions:', formattedTransactions.length);
      console.log('Transaction IDs being sent:', formattedTransactions.map(t => ({
        id: t.id,
        description: t.description || 'N/A'
      })));
      const idsBeingSent = formattedTransactions.map(t => t.id).filter(Boolean);
      console.log('Unique IDs being sent:', [...new Set(idsBeingSent)]);
      
      // Final validation: Check for missing IDs on existing transactions
      const existingTransactionsWithoutIds = formattedTransactions.filter(
        (tx, idx) => !tx.id && transactionsToUse[idx]?.key?.startsWith('backend-')
      );
      if (existingTransactionsWithoutIds.length > 0) {
        console.error('CRITICAL ERROR: Some existing transactions are missing IDs in formatted data:', existingTransactionsWithoutIds);
        message.error('Error: Some transactions are missing IDs. Please refresh and try again.');
        setLoading(false);
        return;
      }

      // Add file_hash and file_path from the processed file response
      if (initialValues && initialValues.statement_data) {
        formattedData.file_hash = initialValues.statement_data.file_hash;
        formattedData.file_path = initialValues.statement_data.file_path;
      } else {
        // For manually created statements (no file upload), generate a unique hash
        // Use timestamp + bank + statement_date + random to ensure uniqueness
        const hashInput = `${Date.now()}_${
          values.bank
        }_${values.statement_date.format("YYYY-MM-DD")}_${
          formattedTransactions.length
        }_${Math.random()}`;
        // Simple hash generation (browser-compatible)
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
          const char = hashInput.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        // Ensure hash is within 32 character limit
        const hexHash = Math.abs(hash).toString(16).padStart(8, "0");
        formattedData.file_hash = hexHash.substring(0, 32);
      }

      formattedData.transactions = formattedTransactions;
      
      // Debug logging for transaction IDs being sent
      console.log('=== SAVING STATEMENT ===');
      console.log('Statement ID:', statementId);
      console.log('Form transactions count:', transactionsToUse.length);
      console.log('Formatted transactions count:', formattedTransactions.length);
      console.log('Form transactions (raw):', transactionsToUse.map(t => ({
        id: t.id,
        description: t.description,
        amount: t.amount,
        hasId: !!t.id
      })));
      console.log('Formatted transactions (with IDs):', formattedTransactions.map(t => ({
        id: t.id,
        description: t.description,
        amount: t.amount,
        hasId: !!t.id
      })));
      console.log('Transaction IDs being sent:', formattedTransactions.map(t => t.id).filter(Boolean));
      console.log('Full formatted data:', JSON.stringify(formattedData, null, 2));

      let response;
      if (isEditing && statementId) {
        response = await updateStatement(statementId, formattedData);
        
        // Check for warnings in response (transactions that couldn't be deleted)
        if (response?.warnings && Array.isArray(response.warnings) && response.warnings.length > 0) {
          Modal.warning({
            title: "Statement Updated with Warnings",
            width: 600,
            content: (
              <div>
                <p>{response.message || "Statement updated successfully, but some transactions could not be deleted:"}</p>
                <ul style={{ marginTop: 16, marginBottom: 16 }}>
                  {response.warnings.map((msg, idx) => (
                    <li key={idx} style={{ marginBottom: 8 }}>{msg}</li>
                  ))}
                </ul>
                <p>Please unallocate these transactions before deleting them.</p>
              </div>
            ),
            onOk: () => {
              // Refresh statement data after warnings are acknowledged
              if (statementId) {
                fetchStatementDetails(statementId);
              }
            }
          });
        } else {
          message.success("Statement updated successfully");
          // Refresh statement data to reflect deletions
          if (statementId) {
            await fetchStatementDetails(statementId);
          }
        }
      } else {
        response = await createStatement(formattedData);
        message.success("Statement created successfully");
      }

      if (typeof onSuccess === "function") {
        onSuccess(response);
        // Reload page to fetch fresh data including newly created statement
        window.location.reload();
      } else {
        navigate("/accounting/transactions-allocations");
      }
    } catch (error) {
      console.error("Error saving statement:", error);
      
      // Handle validation errors for transactions with allocations
      if (error.response?.data?.transactions && Array.isArray(error.response.data.transactions)) {
        const errorMessages = error.response.data.transactions;
        Modal.error({
          title: "Cannot Delete Allocated Transactions",
          width: 600,
          content: (
            <div>
              <p>The following transactions cannot be deleted because they have allocations:</p>
              <ul style={{ marginTop: 16, marginBottom: 16 }}>
                {errorMessages.map((msg, idx) => (
                  <li key={idx} style={{ marginBottom: 8 }}>{msg}</li>
                ))}
              </ul>
              <p>Please unallocate these transactions before deleting them.</p>
            </div>
          ),
        });
      } else {
        message.error(
          "Failed to save statement: " +
            (error.response?.data?.error || 
             error.response?.data?.detail ||
             error.message)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (info) => {
    if (info.file) {
      setFileObj(info.file.originFileObj || info.file);
    }
  };

  const showFileProcessingModal = () => {
    if (!fileObj) {
      message.error("Please select a file first");
      return;
    }
    setFileProcessingModalVisible(true);
  };

  const handleProcessFile = async () => {
    try {
      setLoading(true);

      // Get form values
      const formValues = form.getFieldsValue([
        "bank",
        "statement_date",
        "start_date",
        "notes",
      ]);

      // Validate required fields
      if (!formValues.bank || !formValues.statement_date) {
        message.error(
          "Please fill in all required fields (Bank, Statement Date)"
        );
        setLoading(false);
        return;
      }

      // Format data for API
      const statementData = {
        bank: formValues.bank,
        statement_date: formValues.statement_date.format("YYYY-MM-DD"),
        start_date: formValues.start_date
          ? formValues.start_date.format("YYYY-MM-DD")
          : formValues.statement_date.format("YYYY-MM-DD"),
        end_date: formValues.statement_date.format("YYYY-MM-DD"), // Use statement_date as end_date
        brand: selectedBrand?.id || 1, // Use selected brand or default to 1
        notes: formValues.notes || " ",
      };

      console.log("Statement data for NOTES:", formValues.notes);
      // Process file on backend
      const response = await processStatementFile(fileObj, statementData);
      console.log(
        "Response from processStatementFile method for NOTES: ",
        response
      );
      if (response.file_hash_exists) {
        message.error("This file has already been processed");
        setLoading(false);
        return;
      }

      // Format transactions for form
      const formattedTransactions = response.statement_data.transactions.map(
        (transaction, index) => {
          // Ensure description is a string, not an object
          let descriptionValue = transaction.description || "";
          if (typeof descriptionValue === 'object' && descriptionValue !== null) {
            descriptionValue = descriptionValue.description || descriptionValue.toString() || "";
          }
          if (typeof descriptionValue !== 'string') {
            descriptionValue = String(descriptionValue || "");
          }
          
          return {
            ...transaction,
            // CRITICAL: Use ID + index for unique key, or just index if no ID
            key: transaction.id 
              ? `backend-${transaction.id}-${index}` 
              : `backend-new-${index}`,
            transaction_date: dayjs(transaction.transaction_date),
            description: descriptionValue, // Ensure description is always a string
          };
        }
      );

      // Find the earliest and latest transaction dates
      let earliestDate = null;
      let latestDate = null;

      if (formattedTransactions.length > 0) {
        formattedTransactions.forEach((transaction) => {
          const transDate = transaction.transaction_date;
          if (!earliestDate || transDate.isBefore(earliestDate)) {
            earliestDate = transDate;
          }
          if (!latestDate || transDate.isAfter(latestDate)) {
            latestDate = transDate;
          }
        });
      }

      // Set form values
      form.setFieldsValue({
        bank: parseInt(response.statement_data.bank),
        statement_date: dayjs(response.statement_data.statement_date),
        start_date: earliestDate || dayjs(response.statement_data.start_date),
        // end_date removed - using statement_date instead
        opening_balance: response.statement_data.opening_balance,
        closing_balance: response.statement_data.closing_balance,
        notes: response.statement_data?.notes || "",
        transactions: formattedTransactions,
      });
      console.log(
        "Set fields values for NOTES :",
        response.statement_data.notes
      );

      setBankBalance(response.statement_data.bank_balance);

      // Update state
      setIsEditing(true);
      setInitialValues(response);

      // Check if file has already been processed before
      if (response.file_hash_exists) {
        // Handle duplicate file case more prominently
        setFileProcessingModalVisible(false);
        Modal.warning({
          title: "Duplicate Statement File Detected",
          content: (
            <div>
              <p>
                This exact file has already been processed and saved in the
                system.
                {response.file_hash && (
                  <span>
                    {" "}
                    The file hash <strong>{response.file_hash}</strong> already
                    exists in the database.
                  </span>
                )}
              </p>
              <p>
                <strong>Possible actions:</strong>
                <ul>
                  <li>
                    Search for the existing statement in the statements list
                  </li>
                  <li>Use a different file if this was uploaded in error</li>
                  <li>
                    Contact system administrator if you believe this is an error
                  </li>
                </ul>
              </p>
            </div>
          ),
          onOk: () => {
            // Reset form and redirect to statements list
            form.resetFields();
            navigate("/accounting/statements");
          },
        });
        return; // Stop processing to prevent duplicate statement creation
      } else if (
        response.statement_data &&
        response.statement_data.transactions
      ) {
        // Only access transactions if statement_data exists
        message.success(
          `Successfully processed statement with ${response.statement_data.transactions.length} transactions`
        );
      } else {
        message.success("File processed successfully");
      }

      // Show duplicate statistics if available
      if (response.duplicate_stats) {
        const { new_entries, possible_duplicates, possible_updates } =
          response.duplicate_stats;
        message.info(
          `Duplicate detection results: ${new_entries} new entries, ${possible_duplicates} possible duplicates, ${possible_updates} possible updates`
        );
      }

      setFileProcessingModalVisible(false);
    } catch (error) {
      console.error("Error processing file:", error);
      message.error(
        "Failed to process file: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setLoading(false);
    }
  };
  const handleAddTransaction = () => {
    const transactions = form.getFieldValue("transactions") || [];
    const newKey = `new-${Date.now()}-${transactions.length}`;
    const newTransaction = {
      key: newKey,
      transaction_date: dayjs(),
      detail: "DEBIT",
      type: "ACH_DEBIT",
      description: "",
      amount: 0,
      reference: "N/A",
      check_or_slip: "",
      duplicate_status: "NEW_ENTRY",
    };
    const updatedTransactions = [...transactions, newTransaction];
    form.setFieldsValue({ transactions: updatedTransactions });
  };

  const handleDateChange = (date, dateString) => {
    if (date) {
      // Calculate start and end dates based on statement date
      const startDate = dayjs(date).startOf("month");
      // end_date removed - using statement_date instead
      form.setFieldsValue({
        start_date: startDate,
      });
    }
  };

  const updateTransactionField = (transactions, key, field, value) => {
    const updatedTransactions = transactions.map((transaction) => {
      if (transaction.key === key) {
        return { ...transaction, [field]: value };
      }
      return transaction;
    });
    return updatedTransactions;
  };

  // Helper function to get row style based on duplicate status and allocations
  const getRowStyle = (record) => {
    // Safety check - return empty object if record is undefined
    if (!record) return {};

    // Check if transaction has allocations
    const allocatedAmount = parseFloat(record.allocated_amount || 0);
    const overpaymentAmount = parseFloat(record.overpayment_amount || 0);
    const hasAllocations = allocatedAmount > 0 || overpaymentAmount > 0;

    // If has allocations, add a subtle background color
    if (hasAllocations) {
      return { background: "#fff7e6" }; // Light orange to indicate allocated
    }

    // Otherwise, use duplicate status styling
    if (!record.duplicate_status) return {};

    switch (record.duplicate_status) {
      case "NEW_ENTRY":
        return { background: "#f6ffed" }; // Light green for new entries
      case "POSSIBLE_DUPLICATE":
        return { background: "#fff1f0" }; // Light red for possible duplicates
      case "POSSIBLE_UPDATE":
        return { background: "#fffbe6" }; // Light yellow for possible updates
      default:
        return {};
    }
  };

  const transactionColumns = [
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      width: 150,
      render: (date, record) => {
        const handleDateChange = (value) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "transaction_date",
            value ? value.format("YYYY-MM-DD") : null
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        return (
          <DatePicker
            value={date ? dayjs(date) : null}
            onChange={handleDateChange}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "duplicate_status",
      key: "duplicate_status",
      width: 150,
      render: (status, record) => {
        const statusOptions = [
          { value: "NEW_ENTRY", label: "New Entry", color: "success" },
          {
            value: "POSSIBLE_DUPLICATE",
            label: "Possible Duplicate",
            color: "error",
          },
          {
            value: "POSSIBLE_UPDATE",
            label: "Possible Update",
            color: "warning",
          },
        ];

        const currentStatus = status || "NEW_ENTRY";
        const statusOption = statusOptions.find(
          (opt) => opt.value === currentStatus
        );

        const handleStatusChange = (newValue) => {
          Modal.confirm({
            title: "Confirm Status Change",
            content: `Are you sure you want to change the status from "${
              statusOption?.label || currentStatus
            }" to "${
              statusOptions.find((opt) => opt.value === newValue)?.label ||
              newValue
            }"?`,
            onOk: () => {
              const currentTransactions =
                form.getFieldValue("transactions") || [];
              const updatedTransactions = updateTransactionField(
                currentTransactions,
                record.key,
                "duplicate_status",
                newValue
              );
              form.setFieldsValue({ transactions: updatedTransactions });
            },
          });
        };

        return (
          <div>
            <Select
              value={currentStatus}
              onChange={handleStatusChange}
              style={{ width: "100%" }}
            >
              {statusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  <Tag color={option.color}>{option.label}</Tag>
                </Option>
              ))}
            </Select>
            {record.possible_duplicate_or_update_of && (
              <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                Ref: #{record.possible_duplicate_or_update_of}
              </Typography.Text>
            )}
          </div>
        );
      },
    },

    {
      title: "Detail",
      dataIndex: "detail",
      key: "detail",
      width: 150,
      render: (detail, record) => {
        const handleChange = (value) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "detail",
            value
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        return (
          <Select
            value={detail || "DEBIT"}
            onChange={handleChange}
            style={{ width: "100%" }}
          >
            <Option value="DEBIT">DEBIT</Option>
            <Option value="CREDIT">CREDIT</Option>
            <Option value="CHECK">CHECK</Option>
            <Option value="DSLIP">DSLIP</Option>
          </Select>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type, record) => {
        const handleChange = (value) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "type",
            value
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        return (
          <Select
            value={type || "ACH_DEBIT"}
            onChange={handleChange}
            style={{ width: "100%" }}
          >
            <Option value="ACH_DEBIT">ACH DEBIT</Option>
            <Option value="ACH_CREDIT">ACH CREDIT</Option>
            <Option value="WIRE_OUTGOING">WIRE OUTGOING</Option>
            <Option value="CHECK_PAID">CHECK PAID</Option>
            <Option value="CHECK_DEPOSIT">CHECK DEPOSIT</Option>
            <Option value="ACCT_XFER">ACCOUNT TRANSFER</Option>
          </Select>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      render: (description, record) => {
        const handleChange = (e) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "description",
            e.target.value
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        // Ensure description is a string, not an object
        let descriptionValue = "";
        if (typeof description === 'string') {
          descriptionValue = description;
        } else if (typeof description === 'object' && description !== null) {
          descriptionValue = description.description || String(description) || "";
          // Handle case where String(description) returns "[object Object]"
          if (descriptionValue === "[object Object]") {
            descriptionValue = "";
          }
        } else if (description) {
          descriptionValue = String(description);
        }

        return (
          <Input
            value={descriptionValue}
            onChange={handleChange}
            placeholder="Enter description"
          />
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 150,
      render: (amount, record) => {
        const handleChange = (value) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "amount",
            value
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        return (
          <InputNumber
            step={0.01}
            value={parseFloat(amount || 0)}
            onChange={handleChange}
            formatter={(value) => {
              if (value === undefined || value === null) return "$ 0";
              const numValue = typeof value === 'string' ? parseFloat(value) : value;
              if (isNaN(numValue)) return "$ 0";
              const absValue = Math.abs(numValue);
              const formatted = `$ ${absValue}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return numValue < 0 ? `-${formatted}` : formatted;
            }}
            parser={(value) => {
              // Allow negative sign and preserve it
              const cleaned = value.replace(/[^\d.-]/g, "");
              // Handle negative sign at the start
              if (cleaned.startsWith('-')) {
                const numPart = cleaned.substring(1);
                return numPart ? `-${numPart}` : '-';
              }
              return cleaned || '';
            }}
            style={{ width: "100%" }}
            placeholder="0.00"
          />
        );
      },
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      width: 150,
      render: (reference, record) => {
        const handleChange = (e) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "reference",
            e.target.value
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        return (
          <Input
            value={reference || ""}
            onChange={handleChange}
            placeholder="N/A"
          />
        );
      },
    },
    {
      title: "Check/Slip",
      dataIndex: "check_or_slip",
      key: "check_or_slip",
      width: 150,
      render: (check_or_slip, record) => {
        const handleChange = (e) => {
          const currentTransactions = form.getFieldValue("transactions") || [];
          const updatedTransactions = updateTransactionField(
            currentTransactions,
            record.key,
            "check_or_slip",
            e.target.value
          );
          form.setFieldsValue({ transactions: updatedTransactions });
        };

        return (
          <Input
            value={check_or_slip || ""}
            onChange={handleChange}
            placeholder="Optional"
          />
        );
      },
    },
    {
      title: "Allocation Status",
      key: "allocation_status",
      width: 180,
      render: (_, record) => {
        // More robust check for allocations - handle null, undefined, strings, etc.
        const allocatedAmount = parseFloat(record.allocated_amount || 0) || 0;
        const overpaymentAmount = parseFloat(record.overpayment_amount || 0) || 0;
        const hasAllocations = allocatedAmount > 0 || overpaymentAmount > 0;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('Allocation status check:', {
            id: record.id,
            allocated_amount: record.allocated_amount,
            overpayment_amount: record.overpayment_amount,
            allocatedAmount,
            overpaymentAmount,
            hasAllocations
          });
        }
        
        if (!hasAllocations) {
          return <Tag color="success">Unallocated</Tag>;
        }
        
        return (
          <div>
            {allocatedAmount > 0 && (
              <Tag color="blue" style={{ marginBottom: 4, display: "block" }}>
                ${allocatedAmount.toFixed(2)} to invoices
              </Tag>
            )}
            {overpaymentAmount > 0 && (
              <Tag color="orange" style={{ display: "block" }}>
                ${overpaymentAmount.toFixed(2)} to overpayments
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => {
        // Check if transaction has allocations - handle various data types
        const allocatedAmount = parseFloat(record.allocated_amount || 0) || 0;
        const overpaymentAmount = parseFloat(record.overpayment_amount || 0) || 0;
        const hasAllocations = allocatedAmount > 0 || overpaymentAmount > 0;
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('Transaction delete check:', {
            id: record.id,
            key: record.key,
            allocatedAmount,
            overpaymentAmount,
            hasAllocations,
            record: record
          });
        }
        
        return (
          <Button
            type="text"
            danger
            icon={<MinusCircleOutlined />}
            disabled={hasAllocations}
            onClick={() => {
              if (hasAllocations) {
                Modal.warning({
                  title: "Cannot Delete Transaction",
                  content: `This transaction has allocations ($${allocatedAmount.toFixed(2)} allocated to invoices, $${overpaymentAmount.toFixed(2)} allocated to overpayments). Please unallocate all amounts before deleting.`,
                  okText: "OK",
                });
                return;
              }
              
              // For unallocated transactions, delete immediately without confirmation
              const currentTransactions = form.getFieldValue("transactions") || [];
              
              // Get the transaction ID or key to identify which one to remove
              const recordId = record.id ? parseInt(record.id) : null;
              const recordKey = record.key;
              
              // Filter out ONLY the specific transaction being deleted
              const filteredTransactions = currentTransactions.filter((transaction) => {
                // If we have an ID, match by ID (most reliable)
                if (recordId && transaction.id) {
                  const txId = parseInt(transaction.id);
                  if (txId === recordId) {
                    return false; // Remove this one
                  }
                }
                // If we have a key and no ID match, match by key
                if (recordKey && transaction.key === recordKey) {
                  return false; // Remove this one
                }
                // Keep all other transactions
                return true;
              });
              
              // Update form with the filtered list
              form.setFieldsValue({ 
                transactions: filteredTransactions 
              });
              
              message.success("Transaction removed successfully");
            }}
            title={
              hasAllocations
                ? "Cannot delete: Transaction has allocations. Unallocate first."
                : "Delete Transaction"
            }
          />
        );
      },
    },
  ];

  const handleDeleteStatement = () => {
    if (!statementId) {
      message.error("No statement selected to delete");
      return;
    }

    Modal.confirm({
      title: "Delete Statement",
      content:
        "Are you sure you want to delete this statement? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      async onOk() {
        try {
          setLoading(true);
          await deleteStatement(statementId);
          message.success("Statement deleted successfully");

          if (typeof onSuccess === "function") {
            onSuccess({ deleted: true, id: statementId });
          } else {
            navigate("/accounting/transactions-allocations");
          }

          if (typeof onCancel === "function") {
            onCancel();
          }
        } catch (error) {
          console.error("Error deleting statement:", error);
          console.error("Error response:", error.response);
          console.error("Error response data:", error.response?.data);
          
          const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to delete statement";
          const errorDetails = error.response?.data?.details;
          
          // Show detailed error if available
          if (errorDetails && Array.isArray(errorDetails)) {
            Modal.error({
              title: "Cannot Delete Statement",
              width: 600,
              content: (
                <div>
                  <p>{errorMessage || "This statement contains transactions with allocations:"}</p>
                  <ul style={{ marginTop: 16, marginBottom: 16 }}>
                    {errorDetails.map((msg, idx) => (
                      <li key={idx} style={{ marginBottom: 8 }}>{msg}</li>
                    ))}
                  </ul>
                  <p>Please unallocate these transactions before deleting the statement.</p>
                  <p style={{ marginTop: 16, fontSize: "12px", color: "#666" }}>
                    <strong>Note:</strong> To delete individual unallocated transactions, use the trash icon (🗑️) in the Actions column of each transaction row.
                  </p>
                </div>
              ),
            });
          } else if (error.response?.status === 400) {
            // Handle 400 errors with a user-friendly message
            Modal.error({
              title: "Cannot Delete Statement",
              width: 600,
              content: (
                <div>
                  <p>{errorMessage}</p>
                  <p style={{ marginTop: 16 }}>
                    This statement contains transactions with allocations or overpayments. 
                    Please unallocate all transactions before deleting the statement.
                  </p>
                  <p style={{ marginTop: 16, fontSize: "12px", color: "#666" }}>
                    <strong>Note:</strong> To delete individual unallocated transactions, use the trash icon (🗑️) in the Actions column of each transaction row.
                  </p>
                </div>
              ),
            });
          } else {
            Modal.error({
              title: "Error Deleting Statement",
              content: errorMessage,
            });
          }
          // Don't throw error - we've already shown it to the user
          // This prevents the "Uncaught runtime error" from appearing
        } finally {
          setLoading(false);
        }
      },
    });
  };

  if (!selectedBrand) {
    return (
      <Card title={isEditing ? "Edit Statement" : "Create Statement"}>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <h4>Please select a brand to create a statement</h4>
          <Button
            type="primary"
            onClick={() => navigate("/accounting/statements")}
            style={{ marginTop: 16 }}
          >
            Go Back
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={isEditing ? "Edit Statement" : "Create Statement"}
      size="small"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          statement_date: dayjs(),
          start_date: dayjs().startOf("month"),
          // end_date removed - using statement_date instead
          opening_balance: 0,
          transactions: [],
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="bank"
              label="Bank Account"
              rules={[
                { required: true, message: "Please select a bank account" },
              ]}
            >
              <Select
                placeholder="Select a bank account"
                loading={loading}
                onChange={(value) => {
                  const selectedBank = banks.find((bank) => bank.id === value);
                  if (selectedBank) {
                    if (selectedBank.account_balance !== undefined) {
                      setBankBalance(parseFloat(selectedBank.account_balance));
                    } else {
                      setBankBalance(0);
                    }

                    if (
                      selectedBank.latest_statement_closing_balance !==
                      undefined
                    ) {
                      setLatestStatementBalance(
                        parseFloat(
                          selectedBank.latest_statement_closing_balance
                        )
                      );
                    } else {
                      setLatestStatementBalance(0);
                    }
                  }
                }}
              >
                {Array.isArray(banks) && banks.length > 0 ? (
                  banks.map((bank) => (
                    <Option key={bank.id} value={bank.id}>
                      {bank.name} :{" "}
                      {bank.account_number
                        ? bank.account_number
                            .slice(-4)
                            .padStart(bank.account_number.length, "*")
                        : "No account number"}
                    </Option>
                  ))
                ) : (
                  <Option disabled>No banks available</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="statement_date"
              label="Statement Date"
              rules={[
                { required: true, message: "Please select statement date" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="opening_balance"
              label="Opening Balance"
            >
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/[^\d.-]/g, "")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="start_date"
              label="Start Date"
              tooltip="Will be updated to match earliest transaction date after processing file"
              rules={[{ required: true, message: "Please select start date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          {/* End Date field removed - using statement_date instead */}
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="closing_balance"
              label="Closing Balance"
            >
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/[^\d.-]/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginTop: 32 }}>
              <Typography.Text strong>
                Account Balance (All Transactions)
              </Typography.Text>
              <div>
                {`$ ${bankBalance.toLocaleString(undefined, {
                  minimumFractionDigits: getDecimalPrecision(),
                  maximumFractionDigits: getDecimalPrecision(),
                })}`}
              </div>

              <div style={{ marginTop: 16 }}>
                <Typography.Text strong>
                  Latest Statement Balance
                </Typography.Text>
                <div>
                  {`$ ${latestStatementBalance.toLocaleString(undefined, {
                    minimumFractionDigits: getDecimalPrecision(),
                    maximumFractionDigits: getDecimalPrecision(),
                  })}`}
                </div>
              </div>
            </div>
          </Col>
        </Row>

{/* Excel upload removed - transactions are added manually */}

        <Modal
          title="Process Statement File"
          open={fileProcessingModalVisible}
          onOk={handleProcessFile}
          onCancel={() => setFileProcessingModalVisible(false)}
          confirmLoading={loading}
        >
          <p>
            This will process the statement file and extract all transactions.
            The backend will:
          </p>
          <ul>
            <li>Read the Excel file and extract transactions</li>
            <li>Calculate opening and closing balances</li>
            <li>Check for duplicate transactions</li>
            <li>Return the complete statement data with duplicate status</li>
          </ul>
          <p>
            <strong>Required fields:</strong>
          </p>
          <ul>
            <li>Bank</li>
            <li>Statement Date</li>
            <li>Start Date</li>
            {/* End Date removed - using statement_date instead */}
          </ul>
        </Modal>

        <Form.List name="transactions" key={transactionDeletionKey}>
          {(fields, { add, remove }) => {
            // Store remove function in ref so delete handler can use it
            formListRemoveRef.current = remove;
            
            // CRITICAL: Get transactions directly from form values
            // This ensures we always have the latest state, including any deletions
            const formTransactions = form.getFieldValue("transactions") || [];
            
            // Use fields array length as the source of truth for rendering
            // But use formTransactions for actual data
            const allTransactions = formTransactions;
            
            // CRITICAL: Sync fields array with form values if they're out of sync
            // This can happen when we manually update form state
            if (fields.length !== allTransactions.length) {
              console.warn('Form.List fields and transactions are out of sync:', {
                fieldsLength: fields.length,
                transactionsLength: allTransactions.length,
                syncingFields: true
              });
              
              // Force sync by updating form with current form values
              // This ensures Form.List reflects the actual form state
              setTimeout(() => {
                const currentValues = form.getFieldValue("transactions") || [];
                if (currentValues.length !== fields.length) {
                  console.log('Syncing Form.List fields with form values');
                  // The form values are the source of truth
                  // Form.List will re-render with the correct number of fields
                }
              }, 0);
            }
            
            // Create columns with access to remove function
            const columnsWithRemove = transactionColumns.map(col => {
              if (col.key === "actions") {
                return {
                  ...col,
                  render: (_, record) => {
                    // Check if transaction has allocations
                    const allocatedAmount = parseFloat(record.allocated_amount || 0) || 0;
                    const overpaymentAmount = parseFloat(record.overpayment_amount || 0) || 0;
                    const hasAllocations = allocatedAmount > 0 || overpaymentAmount > 0;
                    
                    // Debug logging for unallocated transactions
                    if (!hasAllocations && process.env.NODE_ENV === 'development') {
                      console.log('Unallocated transaction delete button clicked:', {
                        id: record.id,
                        key: record.key,
                        description: record.description,
                        amount: record.amount,
                        allTransactionsLength: allTransactions.length,
                        fieldsLength: fields.length
                      });
                    }
                    
                    return (
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        disabled={hasAllocations}
                        onClick={() => {
                          if (hasAllocations) {
                            Modal.warning({
                              title: "Cannot Delete Transaction",
                              content: `This transaction has allocations ($${allocatedAmount.toFixed(2)} allocated to invoices, $${overpaymentAmount.toFixed(2)} allocated to overpayments). Please unallocate all amounts before deleting.`,
                              okText: "OK",
                            });
                            return;
                          }
                          
                          // For unallocated transactions, delete immediately
                          // SIMPLIFIED APPROACH: Directly filter by ID and allocation status
                          const recordId = record.id ? parseInt(record.id) : null;
                          const recordKey = record.key;
                          
                          // Get the record's allocation status to ensure we match the right transaction
                          const recordAllocatedAmount = parseFloat(record.allocated_amount || 0) || 0;
                          const recordOverpaymentAmount = parseFloat(record.overpayment_amount || 0) || 0;
                          
                          console.log('=== DELETING TRANSACTION (SIMPLIFIED) ===');
                          console.log('Record to delete:', {
                            id: recordId,
                            key: recordKey,
                            description: record.description,
                            amount: record.amount,
                            allocated_amount: recordAllocatedAmount,
                            overpayment_amount: recordOverpaymentAmount
                          });
                          
                          // Get current form values
                          const currentTransactions = [...(form.getFieldValue("transactions") || [])];
                          console.log('Current transactions before deletion:', currentTransactions.length);
                          console.log('All transaction IDs:', currentTransactions.map((t, idx) => ({
                            idx,
                            id: t?.id,
                            key: t?.key,
                            desc: t?.description,
                            allocated: parseFloat(t?.allocated_amount || 0),
                            overpayment: parseFloat(t?.overpayment_amount || 0)
                          })));
                          
                          // Filter out the transaction by matching ID + allocation status
                          // CRITICAL: This handles duplicate transactions (same ID, different allocation status)
                          // by matching on BOTH ID and allocation amounts to ensure we delete the exact one
                          const filteredTransactions = currentTransactions.filter((tx) => {
                            // Priority 1: Match by key (most unique identifier)
                            if (recordKey && tx.key === recordKey) {
                              console.log('Matched by key:', recordKey);
                              return false; // Exclude this transaction
                            }
                            
                            // Priority 2: Match by ID + allocation status (for duplicates with same ID)
                            if (recordId && tx.id) {
                              const txId = parseInt(tx.id);
                              if (txId === recordId) {
                                // Same ID - MUST check allocation status to distinguish duplicates
                                const txAllocated = parseFloat(tx.allocated_amount || 0) || 0;
                                const txOverpayment = parseFloat(tx.overpayment_amount || 0) || 0;
                                const recordAllocated = recordAllocatedAmount;
                                const recordOverpayment = recordOverpaymentAmount;
                                
                                // Only match if BOTH allocation amounts are exactly the same
                                // This ensures we delete the exact transaction, not its duplicate
                                if (txAllocated === recordAllocated && txOverpayment === recordOverpayment) {
                                  console.log('Matched by ID + allocation status:', {
                                    txId,
                                    txAllocated,
                                    txOverpayment,
                                    recordAllocated,
                                    recordOverpayment
                                  });
                                  return false; // Exclude this transaction
                                } else {
                                  // Same ID but different allocation status - this is a duplicate, keep it
                                  console.log('Same ID but different allocation - keeping duplicate:', {
                                    txId,
                                    txAllocated,
                                    txOverpayment,
                                    recordAllocated,
                                    recordOverpayment
                                  });
                                  return true; // Keep this duplicate (different allocation status)
                                }
                              }
                            }
                            
                            // Keep all other transactions (different IDs, no match)
                            return true;
                          });
                          
                          console.log('Filtered transactions after deletion:', filteredTransactions.length);
                          console.log('Removed transaction IDs:', currentTransactions
                            .filter(tx => !filteredTransactions.includes(tx))
                            .map(tx => ({ id: tx.id, key: tx.key, desc: tx.description })));
                          
                          // CRITICAL: Update form state directly - this is the source of truth
                          form.setFieldsValue({ transactions: filteredTransactions });
                          
                          // Force Form.List to re-render
                          setTransactionDeletionKey(prev => prev + 1);
                          
                          // Verify deletion worked
                          setTimeout(() => {
                            const afterDelete = form.getFieldValue("transactions") || [];
                            console.log('Verification after deletion:', {
                              expectedCount: currentTransactions.length - 1,
                              actualCount: afterDelete.length,
                              deletedSuccessfully: afterDelete.length === currentTransactions.length - 1
                            });
                            
                            if (afterDelete.length !== currentTransactions.length - 1) {
                              console.error('ERROR: Deletion did not work! Forcing update...');
                              form.setFieldsValue({ transactions: filteredTransactions });
                              setTransactionDeletionKey(prev => prev + 1);
                            }
                          }, 50);
                          
                          message.success("Transaction removed successfully");
                        }}
                        title={
                          hasAllocations
                            ? "Cannot delete: Transaction has allocations. Unallocate first."
                            : "Delete Transaction"
                        }
                      />
                    );
                  }
                };
              }
              return col;
            });

            return (
              <div>
                <Table
                  columns={columnsWithRemove}
                  dataSource={allTransactions}
                  pagination={false}
                  rowKey={(record, index) => record.key || `transaction-${index}`}
                  size="small"
                  bordered
                  scroll={{ x: "max-content", y: 400 }}
                  rowClassName={(record) => {
                    if (record.duplicate_status === "POSSIBLE_DUPLICATE")
                      return "duplicate-row";
                    if (record.duplicate_status === "POSSIBLE_UPDATE")
                      return "update-row";
                    return "";
                  }}
                  onRow={(record) => ({
                    style: getRowStyle(record),
                  })}
                />
                <Button
                  type="dashed"
                  onClick={handleAddTransaction}
                  style={{ marginBottom: 16, marginTop: 16 }}
                  icon={<PlusOutlined />}
                >
                  Add Transaction
                </Button>
              </div>
            );
          }}
        </Form.List>

        <Form.Item name="notes" label="Notes">
          <TextArea rows={4} placeholder="Enter any additional notes here" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
            >
              Save Statement
            </Button>
            {isEditing && (
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={loading}
                onClick={handleDeleteStatement}
              >
                Delete
              </Button>
            )}
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default CreateStatement;
